// The maxFrameDelay texts quote the thresholds the detection decides on, so they are interpolated from the
// same constants the chart draws its lines from instead of being written out again.
import {
   DETECTION_WINDOW_IN_MINUTES,
   EXPECTED_DATAPOINTS_PER_WINDOW,
   MFD_MAX_DISPLAY_RANGE_IN_MS,
   MFD_THRESHOLD,
   STALL_COUNT_PER_WINDOW,
   STALL_DURATION_IN_SECONDS,
} from './MaxFrameDelayStats';

const GENERAL = 'General';
const STABILITY = 'Stability';
const PERFORMANCE = 'Performance';
const INFRASTRUCTURE = 'Infrastructure';

export const ALERT_CODE = {
   STREAM_OFFLINE: '21001',
   CONTINUOUS_RESTARTS: '22000',
   CONTINUOUS_RESTARTS_CRITICAL_PERFORMANCE: '22001',
   INACTIVE_INGESTS: '22010',
   INACTIVE_AND_ACTIVE_INGESTS: '22011',
   STR_MODERATE_PERFORMANCE: '23001',
   STR_CRITICAL_PERFORMANCE: '23002',
   MFD_PERSISTENT_DELAY_ULL: '23011',
   MFD_PERSISTENT_DELAY: '23012',
   MFD_STALLS: '23013',
   DUPLICATED_INGEST: '24100',
   DUPLICATED_INGEST_DIFFERENT_INFRASTRUCTURE: '24101',
   DUPLICATED_INGEST_DIFFERENT_LOCATIONS: '24102',
   DUPLICATED_INGEST_DIFFERENT_IPS: '24103',
};

// A notification accumulates every code it ever carried, so a single row can hold several. The widget and
// the details panel have to agree on which one describes the alert, and 'highest code' is not that one for
// maxFrameDelay: 23013 (stalls) and 23012 (persistent delay) are both critical, and the persistent delay is
// the more actionable cause - it is what the backend detection reports when both conditions hold.
const ALERT_CODE_PRECEDENCE = [
   ALERT_CODE.MFD_PERSISTENT_DELAY,
   ALERT_CODE.MFD_STALLS,
   ALERT_CODE.MFD_PERSISTENT_DELAY_ULL,
];

export function getPrimaryAlertCode(codes) {
   const codesAsStrings = (codes || []).map(String);
   const byPrecedence = ALERT_CODE_PRECEDENCE.find((code) => codesAsStrings.includes(code));
   // every other alert type keeps the previous behaviour, where the highest code is the relevant one
   return byPrecedence !== undefined ? byPrecedence : codesAsStrings.sort((a, b) => Number(b) - Number(a))[0];
}

// The alerts a notification carried, most relevant first, as titles taken straight from ALERT_MAPPER so a
// table row reads exactly like the widget headline below it.
//
// Driven by 'codes' rather than by 'notification.name' on purpose. A notification accumulates both as arrays
// and neither has a documented order (they arrive as Elasticsearch term buckets, i.e. by document count), so
// the only way to name one of several deterministically is to apply an explicit rule - and the rule already
// exists for codes, as getPrimaryAlertCode. Codes are also stable across the kebab-case rename of the names,
// which keeps notifications from before it readable without a second, legacy lookup table.
export function getAlertTitlesByPrecedence(codes) {
   const primaryCode = getPrimaryAlertCode(codes);
   const furtherCodes = (codes || [])
      .map(String)
      .filter((code) => code !== primaryCode)
      .sort((a, b) => Number(b) - Number(a));

   return [primaryCode, ...furtherCodes].map((code) => ALERT_MAPPER[code]?.title).filter((title) => !!title);
}

const ALERT_CATEGORY_DESCRIPTION_MAPPER = {
   [GENERAL]: 'General alerts include basic issues related to unexpected ingest behavior.',
   [STABILITY]:
      'Stream issues that may appear as a consequence of irregular communication problems with the nanoStream Cloud can be identified through stability alerts. Therefore the stream restart attempts as well as the active connections are analysed.',
   [PERFORMANCE]:
      'Either bandwidth issues or insufficient encoder/computing performances of the ingest source could be observed.',
   [INFRASTRUCTURE]:
      'Infrastructure alerts represent issues that may be invisble from the outside of our infrastructure and even if there are no direct impairments stream performance/quality wise, these issues can lead to critical problems. Alerts of this category serve especially as early notification system, so taking action is immediately possible. Occuring problems with the underlying Infrastructure of the corresponding stream are detected by analysing just the passthrough (non-transcoding) streams. Finding multiple references on different server instances fires a Duplicated Ingest Alert. Depending on the passthrough ingest location or source, the specific alert varies slightly.',
};

//    title => custom widget title
//    alertInfo => custom info for widget hint top right corner
//    category => code category definition from docs
//    categoryDescription => corresponding description from docs
//    recommendation => code specific recommended action
//    custom => special fields tied to a specific alert code; structure can differ;

export const ALERT_MAPPER = {
   [ALERT_CODE.STREAM_OFFLINE]: {
      // no widget renders for this type, so the title is only read by the alert table
      title: 'Stream Offline',
      alertInfo: 'This stream showed unexpected ingest behavior.',
      category: 'General Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[GENERAL],
      recommendation: 'Restart the ingest stream.',
   },
   [ALERT_CODE.CONTINUOUS_RESTARTS]: {
      title: 'Continuous Restarts',
      alertInfo: 'This stream did restart too often within 5 minutes.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'activity', // path to fetch rtmp stats regarding elapsed time between restarts
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000, // 1 hour in milliseconds,
   },
   [ALERT_CODE.CONTINUOUS_RESTARTS_CRITICAL_PERFORMANCE]: {
      title: 'Continuous Restarts with Critical Stream Time Ratio',
      alertInfo: 'This stream did restart too often within 5 minutes while its stream time ratio was critical.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'activity', // path to fetch rtmp stats regarding elapsed time between restarts
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000, // 1 hour in milliseconds,
   },
   [ALERT_CODE.INACTIVE_INGESTS]: {
      title: 'Inactive Ingest',
      alertInfo: 'The ingest stream is connected but not actively sending data.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation:
         'Check your encoder or streaming source configuration. Ensure the source is actively sending data to the stream.',
   },
   [ALERT_CODE.INACTIVE_AND_ACTIVE_INGESTS]: {
      title: 'Inactive and Active Ingests',
      alertInfo: 'The ingest stream has both inactive and active connections.',
      category: 'Stability Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[STABILITY],
      recommendation:
         'Check your encoder or streaming source configuration. Some connections are not actively sending data while others are. Close the inactive connections.',
   },
   [ALERT_CODE.STR_MODERATE_PERFORMANCE]: {
      title: 'Moderate Stream Time Ratio',
      alertInfo:
         'The average stream time ratio (STR) of this stream was between 0.93 and 0.86 for longer than 1 minute.',
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'streamTimeRatio',
      isExtendedDatapointRequired: true,
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000 * 2, // 2 hours in milliseconds,
   },
   [ALERT_CODE.STR_CRITICAL_PERFORMANCE]: {
      title: 'Critical Stream Time Ratio',
      alertInfo: 'The average stream time ratio (STR) of this stream was below 0.86 for longer than 1 minute.',
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: 'Restart the ingest stream.',
      metricPath: 'streamTimeRatio',
      isExtendedDatapointRequired: true,
      maxRtmpStatDisplayRangeInMS: 60 * 60 * 1000 * 2, // 2 hours in milliseconds,
   },
   // The three maxFrameDelay alerts share one type and one chart - they differ by effect, not by a severity
   // ladder, so only title, info and the recommended action vary. The metric path is not listed here: the
   // widget evaluates the audio track for audio only streams, so it resolves the path itself.
   [ALERT_CODE.MFD_PERSISTENT_DELAY]: {
      title: 'Persistently High Frame Delay',
      // prettier-ignore
      alertInfo: `The median frame delay of this stream was at or above ${MFD_THRESHOLD.PERSISTENT_DELAY_MS} ms over a ${DETECTION_WINDOW_IN_MINUTES} minute window. At that level the added latency affects every viewer.`,
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: [
         'This alert means the ingest delivered frames with a consistently high delay, which increases end-to-end latency for all viewers.',
         'Check the encoder settings for an oversized output buffer or a keyframe interval that is too long.',
         'Check the upstream bandwidth. A sustained delay usually means the encoder cannot push its configured bitrate.',
         'Restart the stream after correcting the settings so the new configuration takes effect.',
      ],
      maxRtmpStatDisplayRangeInMS: MFD_MAX_DISPLAY_RANGE_IN_MS,
   },
   [ALERT_CODE.MFD_PERSISTENT_DELAY_ULL]: {
      title: 'Persistently Elevated Frame Delay (Ultra Low Latency)',
      // prettier-ignore
      alertInfo: `The median frame delay of this stream was between ${MFD_THRESHOLD.PERSISTENT_DELAY_ULL_MS} ms and ${MFD_THRESHOLD.PERSISTENT_DELAY_MS} ms over a ${DETECTION_WINDOW_IN_MINUTES} minute window. Regular playback is not affected at that level, only ultra low latency playback.`,
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: [
         'This alert means the ingest delay is moderately elevated. Regular playback is not affected, only applications that rely on ultra low latency.',
         'If you do not run ultra low latency playback, no action is required.',
         'Otherwise reduce the encoder output buffer and verify that the upstream connection has headroom above the configured bitrate.',
      ],
      maxRtmpStatDisplayRangeInMS: MFD_MAX_DISPLAY_RANGE_IN_MS,
   },
   [ALERT_CODE.MFD_STALLS]: {
      title: 'Recurring Ingest Stalls',
      // prettier-ignore
      alertInfo: `At least ${STALL_COUNT_PER_WINDOW} of the ${EXPECTED_DATAPOINTS_PER_WINDOW} datapoints of a ${DETECTION_WINDOW_IN_MINUTES} minute window exceeded ${MFD_THRESHOLD.STALL_MS} ms, so the ingest stalled for at least ${STALL_DURATION_IN_SECONDS} seconds of those ${DETECTION_WINDOW_IN_MINUTES} minutes. This usually points to the network connection rather than the encoder.`,
      category: 'Performance Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[PERFORMANCE],
      recommendation: [
         // prettier-ignore
         `This alert means the ingest repeatedly stalled for short periods. Individual frames arrived more than ${MFD_THRESHOLD.STALL_MS} ms late while the overall delay stayed acceptable.`,
         'Check the network path between encoder and ingest server for packet loss, bandwidth drops or an unstable uplink. An intermittent connection is the most common cause.',
         'If the uplink is shared, check whether other traffic saturates it during the reported periods.',
         'Restarting the stream can help if the encoder itself is the source, but it will not resolve an unstable network path.',
      ],
      maxRtmpStatDisplayRangeInMS: MFD_MAX_DISPLAY_RANGE_IN_MS,
   },
   [ALERT_CODE.DUPLICATED_INGEST]: {
      title: 'Duplicated Ingests',
      alertInfo: 'More than 1 ingest source detected.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      custom: {
         propLabel: 'Unknown',
      },
   },
   [ALERT_CODE.DUPLICATED_INGEST_DIFFERENT_INFRASTRUCTURE]: {
      title: 'Duplicated Ingests (Multiple Infrastructures)',
      alertInfo: 'This stream is meant to ingest via WebRTC protocol, but ingests via rtmp instead.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      custom: {
         propLabel: 'Infrastructure',
      },
   },
   [ALERT_CODE.DUPLICATED_INGEST_DIFFERENT_LOCATIONS]: {
      title: 'Duplicated Ingests (Multiple Locations)',
      alertInfo: 'More than 1 ingest source detected, ingesting from multiple geographic locations.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      connectionProp: 'city',
      custom: {
         propLabel: 'Location',
      },
   },
   [ALERT_CODE.DUPLICATED_INGEST_DIFFERENT_IPS]: {
      title: 'Duplicated Ingests (Multiple IPs)',
      alertInfo: 'More than 1 ingest source detected, ingesting from multiple IPs.',
      category: 'Infrastructure Alert',
      categoryDescription: ALERT_CATEGORY_DESCRIPTION_MAPPER[INFRASTRUCTURE],
      recommendation:
         'Check your ingest sources and insure, that only 1 source is ingesting to this stream name. Restart the ingest stream, if necessary.',
      connectionProp: 'ip',
      custom: {
         propLabel: 'IPv4 Address',
      },
   },
};
